import { supabase } from "@/shared/integrations/supabase/client";
import { sameStorageObject } from "@/shared/lib/firebaseStorageUrl";
import {
  categoryForTag,
  tagsArrayForTag,
  type AssetPhotoTag,
} from "./assetPhotoTags.logic";

export interface SetAssetPhotoTagInput {
  /** `asset_documents.id`. */
  photoId: string;
  /** `property_assets.id` the photo belongs to. */
  assetId: string;
  /** The photo's stored `file_path`. */
  filePath: string;
  tag: AssetPhotoTag;
}

/**
 * Tag an asset photo as the nameplate or a QR code, or clear its tag.
 *
 * Writes `asset_documents.category` + `.tags`, and keeps
 * `property_assets.nameplate_photo_url` in step: `process-asset-ai` reads that
 * column *first* and only falls back to scanning categories, so without it a
 * nameplate tag wouldn't actually steer the AI pipeline.
 *
 * Caveat worth knowing: a later Firebase sync of this asset recomputes
 * `nameplate_photo_url` from the Firestore photo labels and can overwrite the
 * column. The `asset_documents.category` edit is durable — `planAssetPhotoWrites`
 * only ever inserts rows or heals `ref_id`/`mime_type`/`file_size`, never
 * `category`.
 */
export async function setAssetPhotoTag({
  photoId,
  assetId,
  filePath,
  tag,
}: SetAssetPhotoTagInput): Promise<void> {
  const { error: docError } = await supabase
    .from("asset_documents")
    .update({ category: categoryForTag(tag), tags: tagsArrayForTag(tag) })
    .eq("id", photoId);

  if (docError) throw docError;

  if (tag === "tag") {
    const { error } = await supabase
      .from("property_assets")
      .update({ nameplate_photo_url: filePath })
      .eq("id", assetId);
    if (error) throw error;
    return;
  }

  // Clearing or switching away from nameplate: only vacate the column when it
  // still points at this photo, so we never wipe another photo's claim.
  const { data, error: readError } = await supabase
    .from("property_assets")
    .select("nameplate_photo_url")
    .eq("id", assetId)
    .maybeSingle();

  if (readError) throw readError;

  if (sameStorageObject(data?.nameplate_photo_url, filePath)) {
    const { error } = await supabase
      .from("property_assets")
      .update({ nameplate_photo_url: null })
      .eq("id", assetId);
    if (error) throw error;
  }
}
